import React, { useState, useEffect } from 'react';
import { Trophy, Gift, Clock, Eye, LogOut, User, Lock, ArrowLeft, Copy, Star, Building } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useCustomerAuth } from '../../contexts/CustomerAuthContext';
import { customerService } from '../../services/customerService';
import { pointsService } from '../../services/pointsService';
import { getAvailableRewards } from '../../services/rewardsService';
import { formatters } from '../../utils/formatters';
import LoadingSpinner from '../common/LoadingSpinner';
import PointsExpirationInfo from '../common/PointsExpirationInfo';
import CustomerRewards from './CustomerRewards';

const CustomerDashboard = () => {
  const { customer, logout } = useCustomerAuth();
  const [companiesData, setCompaniesData] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [pointsHistory, setPointsHistory] = useState([]);
  const [redemptions, setRedemptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [expirationInfo, setExpirationInfo] = useState({});
  const [copiedCode, setCopiedCode] = useState(null);
  const [allCompaniesStats, setAllCompaniesStats] = useState([]);

  useEffect(() => {
    if (customer) {
      loadCustomerData();
    }
  }, [customer]);

  const loadCustomerData = async () => {
    try {
      setLoading(true);

      // Buscar dados do cliente em todas as empresas
      const allCompaniesData = [];
      
      for (let i = 0; i < customer.customerIds.length; i++) {
        const customerId = customer.customerIds[i];
        const companyId = customer.companyIds[i];
        
        try {
          // Buscar dados completos do cliente nesta empresa
          const customerData = await customerService.getById(customerId);
          
          // Buscar dados da empresa
          const companyData = await customerService.getCompanyData(companyId);
          
          allCompaniesData.push({
            customerId,
            companyId,
            customerData,
            companyData: companyData || { name: `Empresa ${companyId}`, id: companyId }
          });
        } catch (error) {
          console.error(`Erro ao carregar dados da empresa ${companyId}:`, error);
        }
      }
      
      setCompaniesData(allCompaniesData);
      
      // Carregar estatísticas de todas as empresas
      await loadAllCompaniesStats(allCompaniesData);
      
      // Selecionar a primeira empresa por padrão
      if (allCompaniesData.length > 0) {
        setSelectedCompany(allCompaniesData[0]);
        await loadCompanySpecificData(allCompaniesData[0]);
      }

    } catch (error) {
      console.error('Erro ao carregar dados:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadCompanySpecificData = async (companyData) => {
    try {
      // Buscar dados atualizados do cliente para obter pontos corretos
      const updatedCustomerData = await customerService.getById(companyData.customerId);
      console.log('Dados atualizados do cliente:', updatedCustomerData);
      
      // Buscar histórico de pontos
      const pointsData = await pointsService.getByCustomer(companyData.customerId);
      setPointsHistory(pointsData);

      // Usar os pontos já calculados pelo customerService em vez de recalcular
      const currentPoints = updatedCustomerData.totalPoints || updatedCustomerData.points || 0;
      console.log('Pontos do cliente (totalPoints):', updatedCustomerData.totalPoints);
      console.log('Pontos do cliente (points):', updatedCustomerData.points);
      console.log('Pontos calculados finais:', currentPoints);

      // Atualizar os dados do selectedCompany com os pontos corretos
      setSelectedCompany(prev => ({
        ...prev,
        customerData: {
          ...updatedCustomerData,
          points: Math.max(0, currentPoints), // Garantir que não seja negativo
          totalPoints: Math.max(0, currentPoints) // Adicionar totalPoints também
        }
      }));

      // Buscar resgates
      const redemptionsData = await customerService.getRedemptions(companyData.customerId);
      setRedemptions(redemptionsData);

      // Buscar informações de expiração
      try {
        const expInfo = await pointsService.getExpirationInfo(companyData.customerId);
        setExpirationInfo(expInfo);
      } catch (expirationError) {
        console.error('Erro ao buscar informações de expiração:', expirationError);
        // Definir informações padrão se houver erro
        setExpirationInfo({
          pointsExpiringInNext30Days: 0,
          nextExpirationDate: null,
          nextExpirationPoints: 0,
          expiredPoints: [],
          hasExpirationEnabled: false
        });
      }
    } catch (error) {
      console.error('Erro ao carregar dados da empresa:', error);
    }
  };

  const loadAllCompaniesStats = async (allCompaniesData) => {
    try {
      const statsPromises = allCompaniesData.map(async (companyData) => {
        try {
          // Buscar histórico de pontos
          const pointsData = await pointsService.getByCustomer(companyData.customerId);
          
          // Calcular pontos atuais
          const currentPoints = pointsData.reduce((total, transaction) => {
            if (transaction.type === 'add' && transaction.points > 0) {
              return total + transaction.points;
            } else if (transaction.type === 'redeem' || transaction.type === 'expired' || transaction.points < 0) {
              return total - Math.abs(transaction.points);
            }
            return total;
          }, 0);

          // Buscar resgates
          const redemptionsData = await customerService.getRedemptions(companyData.customerId);
          
          // Buscar prêmios disponíveis
          let availableRewards = [];
          try {
            availableRewards = await getAvailableRewards(companyData.companyId, Math.max(0, currentPoints));
          } catch (rewardsError) {
            console.warn(`Erro ao buscar prêmios da empresa ${companyData.companyId}:`, rewardsError);
          }

          return {
            companyId: companyData.companyId,
            companyName: companyData.companyData?.name || `Empresa ${companyData.companyId}`,
            customerData: {
              ...companyData.customerData,
              points: Math.max(0, currentPoints)
            },
            stats: {
              points: Math.max(0, currentPoints),
              redemptions: redemptionsData.length,
              referrals: companyData.customerData?.totalReferrals || 0,
              availableRewards: availableRewards.length
            }
          };
        } catch (error) {
          console.error(`Erro ao carregar stats da empresa ${companyData.companyId}:`, error);
          return {
            companyId: companyData.companyId,
            companyName: companyData.companyData?.name || `Empresa ${companyData.companyId}`,
            customerData: companyData.customerData,
            stats: {
              points: 0,
              redemptions: 0,
              referrals: 0,
              availableRewards: 0
            }
          };
        }
      });

      const allStats = await Promise.all(statsPromises);
      setAllCompaniesStats(allStats);
    } catch (error) {
      console.error('Erro ao carregar estatísticas de todas as empresas:', error);
    }
  };

  const handleCompanyChange = async (companyData) => {
    setSelectedCompany(companyData);
    setLoading(true);
    await loadCompanySpecificData(companyData);
    setLoading(false);
  };

  const handleLogout = async () => {
    try {
      await logout();
    } catch (error) {
      console.error('Erro ao fazer logout:', error);
    }
  };

  const handleCopyReferralCode = async (code) => {
    try {
      await navigator.clipboard.writeText(code);
      setCopiedCode(code);
      setTimeout(() => setCopiedCode(null), 2000);
    } catch (error) {
      // Fallback para navegadores mais antigos
      const textArea = document.createElement('textarea');
      textArea.value = code;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopiedCode(code);
      setTimeout(() => setCopiedCode(null), 2000);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center space-x-4">
              <Link 
                to="/" 
                className="text-blue-600 hover:text-blue-800 flex items-center"
              >
                <ArrowLeft className="w-5 h-5 mr-2" />
                Início
              </Link>
              <div>
                <h1 className="text-xl font-semibold text-gray-900">
                  Olá, {customer.name}!
                </h1>
                <p className="text-sm text-gray-600">
                  Sua área de fidelidade
                </p>
              </div>
              
              {/* Seletor de Empresas */}
              {companiesData.length > 1 && (
                <div className="ml-8">
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Empresa:
                  </label>
                  <select
                    value={selectedCompany?.companyId || ''}
                    onChange={(e) => {
                      const companyData = companiesData.find(c => c.companyId === e.target.value);
                      if (companyData) handleCompanyChange(companyData);
                    }}
                    className="block w-48 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  >
                    {companiesData.map((company) => (
                      <option key={company.companyId} value={company.companyId}>
                        {company.companyData.name}
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </div>
            
            <button
              onClick={handleLogout}
              className="flex items-center space-x-2 text-gray-600 hover:text-gray-800"
            >
              <LogOut className="w-5 h-5" />
              <span>Sair</span>
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Navigation Tabs */}
        <div className="border-b border-gray-200 mb-8">
          <nav className="-mb-px flex space-x-8">
            {[
              { id: 'overview', label: 'Visão Geral', icon: Trophy },
              { id: 'rewards', label: 'Prêmios', icon: Gift },
              { id: 'history', label: 'Histórico', icon: Clock },
              { id: 'profile', label: 'Perfil', icon: User }
            ].map(tab => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`py-2 px-1 border-b-2 font-medium text-sm flex items-center space-x-2 ${
                    activeTab === tab.id
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  <span>{tab.label}</span>
                </button>
              );
            })}
          </nav>
        </div>

        {/* Tab Content */}
        {activeTab === 'overview' && (
          <div className="space-y-6">
            {/* Resumo Total */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              <div className="bg-white p-6 rounded-xl shadow-sm border">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600">Total de Pontos</p>
                    <p className="text-3xl font-bold text-blue-600">
                      {formatters.formatPoints(
                        allCompaniesStats.reduce((total, company) => total + company.stats.points, 0)
                      )}
                    </p>
                  </div>
                  <Trophy className="w-10 h-10 text-blue-500" />
                </div>
              </div>

              <div className="bg-white p-6 rounded-xl shadow-sm border">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600">Total de Resgates</p>
                    <p className="text-3xl font-bold text-green-600">
                      {allCompaniesStats.reduce((total, company) => total + company.stats.redemptions, 0)}
                    </p>
                  </div>
                  <Gift className="w-10 h-10 text-green-500" />
                </div>
              </div>

              <div className="bg-white p-6 rounded-xl shadow-sm border">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600">Total de Indicações</p>
                    <p className="text-3xl font-bold text-purple-600">
                      {allCompaniesStats.reduce((total, company) => total + company.stats.referrals, 0)}
                    </p>
                  </div>
                  <Star className="w-10 h-10 text-purple-500" />
                </div>
              </div>

              <div className="bg-white p-6 rounded-xl shadow-sm border">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600">Empresas Vinculadas</p>
                    <p className="text-3xl font-bold text-orange-600">
                      {allCompaniesStats.length}
                    </p>
                  </div>
                  <Building className="w-10 h-10 text-orange-500" />
                </div>
              </div>
            </div>

            {/* Lista de Empresas */}
            <div className="bg-white rounded-xl shadow-sm border">
              <div className="p-6 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900">
                  Suas Empresas de Fidelidade
                </h3>
                <p className="text-sm text-gray-600 mt-1">
                  Resumo dos seus pontos e atividades em cada empresa
                </p>
              </div>
              
              <div className="divide-y divide-gray-200">
                {allCompaniesStats.map((company) => (
                  <div key={company.companyId} className="p-6 hover:bg-gray-50 transition-colors">
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <h4 className="text-lg font-medium text-gray-900 mb-2">
                          {company.companyName}
                        </h4>
                        
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                          <div className="text-center">
                            <div className="flex items-center justify-center mb-2">
                              <Trophy className="w-5 h-5 text-blue-500 mr-2" />
                              <span className="text-sm font-medium text-gray-600">Pontos</span>
                            </div>
                            <p className="text-xl font-bold text-blue-600">
                              {formatters.formatPoints(company.stats.points)}
                            </p>
                          </div>
                          
                          <div className="text-center">
                            <div className="flex items-center justify-center mb-2">
                              <Gift className="w-5 h-5 text-green-500 mr-2" />
                              <span className="text-sm font-medium text-gray-600">Resgates</span>
                            </div>
                            <p className="text-xl font-bold text-green-600">
                              {company.stats.redemptions}
                            </p>
                          </div>
                          
                          <div className="text-center">
                            <div className="flex items-center justify-center mb-2">
                              <Star className="w-5 h-5 text-purple-500 mr-2" />
                              <span className="text-sm font-medium text-gray-600">Indicações</span>
                            </div>
                            <p className="text-xl font-bold text-purple-600">
                              {company.stats.referrals}
                            </p>
                          </div>
                          
                          <div className="text-center">
                            <div className="flex items-center justify-center mb-2">
                              <Gift className="w-5 h-5 text-orange-500 mr-2" />
                              <span className="text-sm font-medium text-gray-600">Prêmios</span>
                            </div>
                            <p className="text-xl font-bold text-orange-600">
                              {company.stats.availableRewards}
                            </p>
                          </div>
                        </div>

                        {/* Código de Indicação se existir */}
                        {company.customerData?.referralCode && (
                          <div className="mt-4 p-3 bg-blue-50 rounded-lg">
                            <div className="flex items-center justify-between">
                              <div>
                                <p className="text-sm font-medium text-blue-900">Código de Indicação</p>
                                <code className="text-blue-700 font-mono">
                                  {company.customerData.referralCode}
                                </code>
                              </div>
                              <button
                                onClick={() => handleCopyReferralCode(company.customerData.referralCode)}
                                className={`px-3 py-1 rounded-md text-sm flex items-center space-x-1 ${
                                  copiedCode === company.customerData.referralCode
                                    ? 'bg-green-100 text-green-700'
                                    : 'bg-blue-100 text-blue-700 hover:bg-blue-200'
                                }`}
                              >
                                <Copy className="w-3 h-3" />
                                <span>{copiedCode === company.customerData.referralCode ? 'Copiado!' : 'Copiar'}</span>
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Informações de Expiração da empresa selecionada */}
            {selectedCompany && (
              <div className="bg-white rounded-xl shadow-sm border">
                <div className="p-6 border-b border-gray-200">
                  <h3 className="text-lg font-semibold text-gray-900">
                    Expiração de Pontos - {selectedCompany.companyData?.name}
                  </h3>
                </div>
                <div className="p-6">
                  <PointsExpirationInfo expirationInfo={expirationInfo} />
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === 'rewards' && selectedCompany && (
          <CustomerRewards 
            customer={{
              ...selectedCompany.customerData,
              id: selectedCompany.customerId
            }} 
            company={{
              ...selectedCompany.companyData,
              id: selectedCompany.companyId
            }}
          />
        )}

        {activeTab === 'history' && (
          <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">
                Histórico de Pontos
              </h3>
            </div>
            
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Data
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Tipo
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Pontos
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Descrição
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {pointsHistory.map((entry, index) => {
                    // Usar a mesma lógica do painel da empresa
                    // referral_bonus sempre deve ser tratado como positivo, mesmo se o valor estiver negativo por erro
                    const isRedemption = (entry.type === 'redeem' || entry.type === 'expired' || entry.points < 0) && entry.type !== 'referral_bonus';
                    const pointsValue = Math.abs(entry.points);
                    const pointsColor = isRedemption ? 'text-red-600' : 'text-green-600';
                    const pointsPrefix = isRedemption ? '-' : '+';
                    const badgeColor = isRedemption ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800';
                    const badgeText = isRedemption ? 'Resgate/Expirou' : 'Ganhou';
                    
                    return (
                      <tr key={index}>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {formatters.formatDateTime(entry.createdAt)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-2 py-1 text-xs rounded-full ${badgeColor}`}>
                            {badgeText}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <span className={pointsColor}>
                            {pointsPrefix}{formatters.formatPoints(pointsValue)}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-900">
                          {entry.description}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              
              {pointsHistory.length === 0 && (
                <div className="text-center py-8 text-gray-500">
                  Nenhuma movimentação de pontos ainda
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'profile' && (
          <div className="space-y-6">
            <div className="bg-white p-6 rounded-xl shadow-sm border">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Informações Pessoais
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-600">Nome</label>
                  <p className="text-gray-900">{selectedCompany?.customerData?.name}</p>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-600">E-mail</label>
                  <p className="text-gray-900">{customer.email}</p>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-600">CPF/CNPJ</label>
                  <p className="text-gray-900">{formatters.formatCpfOrCnpj(selectedCompany?.customerData?.cpf)}</p>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-600">Telefone</label>
                  <p className="text-gray-900">
                    {selectedCompany?.customerData?.phone ? formatters.formatPhone(selectedCompany.customerData.phone) : '-'}
                  </p>
                </div>
                
                {selectedCompany?.customerData?.birthDate && (
                  <div>
                    <label className="block text-sm font-medium text-gray-600">Data de Nascimento</label>
                    <p className="text-gray-900">{formatters.formatDate(selectedCompany.customerData.birthDate)}</p>
                  </div>
                )}
              </div>
            </div>
            
            <div className="bg-white p-6 rounded-xl shadow-sm border">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Segurança
              </h3>
              
              <button className="flex items-center space-x-2 text-blue-600 hover:text-blue-800">
                <Lock className="w-4 h-4" />
                <span>Alterar Senha</span>
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CustomerDashboard;
